import { useMemo } from "react"
import { useGetOrganizationFeaturesByOrgIdQuery } from "../generated/graphql"
import { isEffectiveViraScribeEnabled } from "./organizationFeatureFlags"
import { useGraphQLClient } from "./useGraphQLClient"
import { useUser } from "./useUser"

const forceViraScribe =
  process.env.REACT_APP_FORCE_VIRA_SCRIBE?.trim().toLowerCase() === "true"
const forceViraLoop =
  process.env.REACT_APP_FORCE_VIRA_LOOP?.trim().toLowerCase() === "true"

/**
 * A lightweight hook to get organization feature flags for the current user's organization
 * @returns { showDBTDiaryFeatures: boolean, showBriteFeatures: boolean, showMVP2Features: boolean, isLoading: boolean }
 */
function useOrganizationFeatureFlags() {
  const { user } = useUser()
  const graphQLClient = useGraphQLClient()
  const orgId = user?.organizations?.[0]?.id ?? null
  const shouldFetch = Boolean(orgId)

  const { data, isLoading } = useGetOrganizationFeaturesByOrgIdQuery(
    graphQLClient,
    { id: orgId ?? -1 },
    { enabled: shouldFetch }
  )

  const showDBTDiaryFeatures = useMemo(
    () =>
      Boolean(
        data?.organization_features?.some(
          (feature) => feature.dbt_diary === true
        )
      ),
    [data?.organization_features]
  )

  const showBriteFeatures: boolean = useMemo(
    () =>
      data?.organization_features.find((orgs) => orgs.brite_features === true)
        ? true
        : false,
    [data?.organization_features]
  )

  const showMVP2Features: boolean = useMemo(
    () =>
      data?.organization_features.find((orgs) => orgs.mvp2_features === true)
        ? true
        : false,
    [data?.organization_features]
  )

  const showViraPathFeatures: boolean = useMemo(
    () =>
      data?.organization_features?.some(
        (feature) => feature.vira_path === true
      ) ?? false,
    [data?.organization_features]
  )

  // Loop implies scribe for UI gates (recording, care-plan). Scribe alone still
  // enables legacy TRANSCRIPT / POST SESSION when Loop is off.
  const showViraScribe: boolean = useMemo(
    () =>
      isEffectiveViraScribeEnabled({
        forceViraScribe,
        forceViraLoop,
        organizationFeatures: data?.organization_features,
      }),
    [data?.organization_features]
  )

  const showViraLoop: boolean = useMemo(
    () =>
      forceViraLoop ||
      (data?.organization_features?.some(
        (feature) => feature.vira_loop === true
      ) ??
        false),
    [data?.organization_features]
  )

  return {
    showDBTDiaryFeatures,
    showBriteFeatures,
    showMVP2Features,
    showViraPathFeatures,
    showViraScribe,
    showViraLoop,
    isLoading,
  }
}

export default useOrganizationFeatureFlags
export { isEffectiveViraScribeEnabled } from "./organizationFeatureFlags"
